import {
    createAsyncThunk,
    createSelector,
    createSlice,
} from "@reduxjs/toolkit";
import GroupsService from "services/GroupsService";
import InstructorsService from "services/InstructorsService";
import SupportCoursesService from "services/SupportCoursesService";
import TutorialsService from "services/TutorialsService";
import TrainingsService from "services/TrainingsService";

export const getInstructors = createAsyncThunk(
    "instructors/getAll",
    async (_, { rejectWithValue }) => {
        try {
            const response = await InstructorsService.getInstructors();
            return response.data.data;
        } catch (error) {
            return rejectWithValue(error.response || "Erreur");
        }
    }
);

export const getInstructorById = createAsyncThunk(
    "instructors/getInstructorById",
    async (id, { rejectWithValue }) => {
        try {
            const response = await InstructorsService.getInstructorById(id);
            return response.data.formateur;
        } catch (error) {
            return rejectWithValue(error.response || "Erreur");
        }
    }
);

export const createInstructor = createAsyncThunk(
    "instructors/createInstructor",
    async (data, { rejectWithValue }) => {
        try {
            const response = await InstructorsService.createInstructor(data);
            return response.data.former;
        } catch (error) {
            return rejectWithValue(error.response.data.error || "Erreur");
        }
    }
);

export const updateInstructorById = createAsyncThunk(
    "instructors/updateInstructorById",
    async ({ id, data }, { rejectWithValue }) => {
        try {
            // TODO: this is supposed to return the updated instructor id, but the API returns only a status message
            const response = await InstructorsService.updateInstructorById(
                id,
                data
            );
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || "Erreur");
        }
    }
);

export const validateInstructorById = createAsyncThunk(
    "instructors/validateInstructorById",
    async (id, { rejectWithValue }) => {
        try {
            const response = await InstructorsService.validateInstructorById(
                id
            );
            return response.data.instructor;
        } catch (error) {
            return rejectWithValue(error.response?.data?.error || "Erreur");
        }
    }
);

export const banInstructorById = createAsyncThunk(
    "instructors/banInstructorById",
    async ({ id, argument }, { rejectWithValue }) => {
        try {
            const response = await InstructorsService.banInstructorById(id, {
                argument,
            });
            return response.data.former;
        } catch (error) {
            return rejectWithValue(error.response?.data?.error || "Erreur");
        }
    }
);

export const deleteInstructorById = createAsyncThunk(
    "instructors/deleteInstructorById",
    async (id, { rejectWithValue }) => {
        try {
            await InstructorsService.deleteInstructorById(id);
            return id;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || "Erreur");
        }
    }
);

// --- Assignation : listes de ce qui est encore assignable au formateur ---

export const getAvailableTrainings = createAsyncThunk(
    "instructors/getAvailableTrainings",
    async ({ id, params } = {}, { rejectWithValue }) => {
        try {
            const response = await InstructorsService.getAvailableTrainings(
                id,
                params
            );
            return response.data.trainings || [];
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message ||
                    error.response?.data?.error ||
                    "Erreur"
            );
        }
    }
);

export const getAvailableSupportCourses = createAsyncThunk(
    "instructors/getAvailableSupportCourses",
    async ({ id, params } = {}, { rejectWithValue }) => {
        try {
            const response =
                await InstructorsService.getAvailableSupportCourses(id, params);
            return response.data.supportCourses || [];
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message ||
                    error.response?.data?.error ||
                    "Erreur"
            );
        }
    }
);

export const getAvailableGroups = createAsyncThunk(
    "instructors/getAvailableGroups",
    async ({ id, params } = {}, { rejectWithValue }) => {
        try {
            const response = await InstructorsService.getAvailableGroups(
                id,
                params
            );
            return response.data.groups || [];
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message ||
                    error.response?.data?.error ||
                    "Erreur"
            );
        }
    }
);

export const getAvailableTutorials = createAsyncThunk(
    "instructors/getAvailableTutorials",
    async ({ id, params } = {}, { rejectWithValue }) => {
        try {
            const response = await InstructorsService.getAvailableTutorials(
                id,
                params
            );
            return response.data.tutorials || [];
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message ||
                    error.response?.data?.error ||
                    "Erreur"
            );
        }
    }
);

// --- Assignation : envoi ---

export const assignTrainingToInstructor = createAsyncThunk(
    "instructors/assignTrainingToInstructor",
    async ({ trainingId, instructorId }, { rejectWithValue }) => {
        try {
            const response = await TrainingsService.assignInstructorToTraining(
                trainingId,
                instructorId
            );
            return response.data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message ||
                    error.response?.data?.error ||
                    "Erreur"
            );
        }
    }
);

export const assignSupportCourseToInstructor = createAsyncThunk(
    "instructors/assignSupportCourseToInstructor",
    async ({ supportCourseId, instructorId }, { rejectWithValue }) => {
        try {
            const response = await SupportCoursesService.assignInstructor(
                supportCourseId,
                instructorId
            );
            return response.data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message ||
                    error.response?.data?.error ||
                    "Erreur"
            );
        }
    }
);

// Le tutoriel n'a qu'un seul enseignant : l'ancien est retiré automatiquement
export const assignTutorialToInstructor = createAsyncThunk(
    "instructors/assignTutorialToInstructor",
    async ({ tutorialId, instructorId }, { rejectWithValue }) => {
        try {
            const response = await TutorialsService.assignInstructor(
                tutorialId,
                instructorId
            );
            return response.data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message ||
                    error.response?.data?.error ||
                    "Erreur"
            );
        }
    }
);

// Attention : le groupe attend `formateurId` (et non `instructorId`) + les données de paiement
export const assignGroupToInstructor = createAsyncThunk(
    "instructors/assignGroupToInstructor",
    async (
        { groupId, instructorId, paymentType, paymentValue },
        { rejectWithValue }
    ) => {
        try {
            const response = await GroupsService.addFormerToGroup(groupId, {
                formateurId: instructorId,
                paymentType,
                paymentValue,
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message ||
                    error.response?.data?.error ||
                    "Erreur"
            );
        }
    }
);

const initialState = {
    instructors: [],
    instructor: null,
    loading: false,
    error: null,
    availableTrainings: [],
    availableSupportCourses: [],
    availableGroups: [],
    availableTutorials: [],
    availableLoading: false,
    availableError: null,
    assigning: false,
};

const instructorsSlice = createSlice({
    name: "instructors",
    initialState,
    reducers: {
        showLoading: (state) => {
            state.loading = true;
        },
        hideLoading: (state, action) => {
            state.loading = false;
        },
        hideError: (state, action) => {
            state.error = null;
        },
        resetAvailable: (state) => {
            state.availableTrainings = [];
            state.availableSupportCourses = [];
            state.availableGroups = [];
            state.availableTutorials = [];
            state.availableLoading = false;
            state.availableError = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // Fetch instructors
            .addCase(getInstructors.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getInstructors.fulfilled, (state, action) => {
                state.loading = false;
                state.instructors = action.payload;
            })
            .addCase(getInstructors.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message;
            })
            // get instructor by id
            .addCase(getInstructorById.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getInstructorById.fulfilled, (state, action) => {
                state.loading = false;
                state.instructor = action.payload;
                state.error = null;
            })
            .addCase(getInstructorById.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // Create Instructor
            .addCase(createInstructor.pending, (state) => {
                state.loading = true;
            })
            .addCase(createInstructor.fulfilled, (state, action) => {
                state.instructors.push(action.payload);
                state.loading = false;
            })
            .addCase(createInstructor.rejected, (state, action) => {
                state.loading = false;
            })
            // Update instructor
            .addCase(updateInstructorById.pending, (state) => {
                state.loading = true;
            })
            .addCase(updateInstructorById.fulfilled, (state, action) => {
                const { _id } = action.payload;
                const index = state.instructors.findIndex(
                    (instructor) => instructor._id === _id
                );
                if (index !== -1) {
                    state.instructors[index] = action.payload;
                }
                state.loading = false;
            })
            .addCase(updateInstructorById.rejected, (state, action) => {
                state.loading = false;
            })
            // validate instructor
            .addCase(validateInstructorById.pending, (state) => {
                state.loading = true;
            })
            .addCase(validateInstructorById.fulfilled, (state, action) => {
                const { _id } = action.payload;
                const index = state.instructors.findIndex(
                    (instructor) => instructor._id === _id
                );
                if (index !== -1) {
                    state.instructors[index] = action.payload;
                }
                state.loading = false;
            })
            .addCase(validateInstructorById.rejected, (state, action) => {
                state.loading = false;
            })
            // ban instructor
            .addCase(banInstructorById.pending, (state) => {
                state.loading = true;
            })
            .addCase(banInstructorById.fulfilled, (state, action) => {
                const { _id } = action.payload;
                const index = state.instructors.findIndex(
                    (instructor) => instructor._id === _id
                );
                if (index !== -1) {
                    state.instructors[index] = action.payload;
                }
                state.loading = false;
            })
            .addCase(banInstructorById.rejected, (state, action) => {
                state.loading = false;
            })
            // Delete instructor
            .addCase(deleteInstructorById.pending, (state) => {
                state.loading = true;
            })
            .addCase(deleteInstructorById.fulfilled, (state, action) => {
                const instructorsId = action.payload;
                state.instructors = state.instructors.filter(
                    (instructors) => instructors._id !== instructorsId
                );
                state.loading = false;
            })
            .addCase(deleteInstructorById.rejected, (state, action) => {
                state.loading = false;
            })
            // Listes assignables
            .addCase(getAvailableTrainings.pending, (state) => {
                state.availableLoading = true;
                state.availableError = null;
            })
            .addCase(getAvailableTrainings.fulfilled, (state, action) => {
                state.availableLoading = false;
                state.availableTrainings = action.payload;
            })
            .addCase(getAvailableTrainings.rejected, (state, action) => {
                state.availableLoading = false;
                state.availableError = action.payload;
            })
            .addCase(getAvailableSupportCourses.pending, (state) => {
                state.availableLoading = true;
                state.availableError = null;
            })
            .addCase(getAvailableSupportCourses.fulfilled, (state, action) => {
                state.availableLoading = false;
                state.availableSupportCourses = action.payload;
            })
            .addCase(getAvailableSupportCourses.rejected, (state, action) => {
                state.availableLoading = false;
                state.availableError = action.payload;
            })
            .addCase(getAvailableGroups.pending, (state) => {
                state.availableLoading = true;
                state.availableError = null;
            })
            .addCase(getAvailableGroups.fulfilled, (state, action) => {
                state.availableLoading = false;
                state.availableGroups = action.payload;
            })
            .addCase(getAvailableGroups.rejected, (state, action) => {
                state.availableLoading = false;
                state.availableError = action.payload;
            })
            .addCase(getAvailableTutorials.pending, (state) => {
                state.availableLoading = true;
                state.availableError = null;
            })
            .addCase(getAvailableTutorials.fulfilled, (state, action) => {
                state.availableLoading = false;
                state.availableTutorials = action.payload;
            })
            .addCase(getAvailableTutorials.rejected, (state, action) => {
                state.availableLoading = false;
                state.availableError = action.payload;
            })
            // Assignations
            .addCase(assignTrainingToInstructor.pending, (state) => {
                state.assigning = true;
            })
            .addCase(assignTrainingToInstructor.fulfilled, (state) => {
                state.assigning = false;
            })
            .addCase(assignTrainingToInstructor.rejected, (state) => {
                state.assigning = false;
            })
            .addCase(assignSupportCourseToInstructor.pending, (state) => {
                state.assigning = true;
            })
            .addCase(assignSupportCourseToInstructor.fulfilled, (state) => {
                state.assigning = false;
            })
            .addCase(assignSupportCourseToInstructor.rejected, (state) => {
                state.assigning = false;
            })
            .addCase(assignTutorialToInstructor.pending, (state) => {
                state.assigning = true;
            })
            .addCase(assignTutorialToInstructor.fulfilled, (state) => {
                state.assigning = false;
            })
            .addCase(assignTutorialToInstructor.rejected, (state) => {
                state.assigning = false;
            })
            .addCase(assignGroupToInstructor.pending, (state) => {
                state.assigning = true;
            })
            .addCase(assignGroupToInstructor.fulfilled, (state) => {
                state.assigning = false;
            })
            .addCase(assignGroupToInstructor.rejected, (state) => {
                state.assigning = false;
            });
    },
});
export const { showLoading, hideLoading, hideError, resetAvailable } =
    instructorsSlice.actions;

// Statut d'un formateur banni (= "supprimé" cote back-office, cf. banInstructorById).
export const INSTRUCTOR_BANNED_STATUS = "BANNED";

// Formateurs selectionnables / assignables : on exclut les formateurs bannis
// (supprimes) pour qu'ils n'apparaissent plus dans les listes déroulantes,
// filtres et affectations. La page "Formateurs" continue d'utiliser
// `state.instructors.instructors` directement pour pouvoir afficher les bannis.
export const selectAssignableInstructors = createSelector(
    (state) => state.instructors.instructors,
    (instructors) =>
        (instructors ?? []).filter(
            (instructor) => instructor?.status !== INSTRUCTOR_BANNED_STATUS
        )
);

export default instructorsSlice.reducer;
